/**
 * Platform branding for every mount (kernel apps, WS-14 section 3.6; layered shell surfaces, WS-8
 * section 3.4, R-22, R-35), moved from the shell's `services/branding-service.ts`, the DOM writer
 * of `platform/applyBranding.ts` and the loader of `platform/deploymentBrand.ts`.
 *
 * One controller per document fetches the org's branding and the deployment tier, applies them to
 * the document through {@link applyBranding}, and serves them read-only to each mount. Logos, names
 * and login copy stay platform branding (R-35); the root shell's colours arrive through
 * `settings`, not here. The controller is itself a `SurfaceBrandingService`, so a host hands it to
 * the chrome's `ChromeServices.branding` as it is (WS-15 R-1 (3)).
 *
 * The pure projections (`accentFor`, `onAccentFor`, the neutral name and favicon) come from
 * `@zion/shell-chrome/model`, so this writer sets the same accent that the chrome reads.
 */
import type { ResolvedPolarity } from '@zion/sdk-contracts';
import {
  DeploymentBrandingSchema,
  OrgBrandingSchema,
  type DeploymentBranding,
  type OrgBranding,
} from '@zion/sdk-cp-dp-contracts';
import type { SurfaceBrandingService, SurfaceBrandingState } from '@zion/sdk-surfaces/v1';
import { NEUTRAL_FAVICON_HREF, NEUTRAL_PRODUCT_NAME, accentFor, onAccentFor } from '@zion/shell-chrome/model';
import { SERVICES_FETCH_TIMEOUT_MS, createCoreApiRequest } from './fetch-policy.js';
import { createListenerSet, type MountScope, type ServiceSession } from './kit.js';
import type { PolarityController } from './polarity.js';

/**
 * The document-wide branding owner.
 *
 * - `current()` returns `{ branding, deployment, resolved }`, starting at `null`, `null`,
 *   `false`. The object keeps its identity until a field changes.
 * - `resolved` turns `true` once the deployment load and the first org load have both settled,
 *   success or failure, and stays `true`.
 * - `subscribe` hears every state change.
 * - `view(scope)` is the mount's read-only {@link SurfaceBrandingService}; its subscriptions end
 *   with the mount.
 * - `dispose()` stops the session and polarity subscriptions, drops every subscriber and makes
 *   any response still in flight write nothing. It leaves the document as it is.
 */
export interface BrandingController extends SurfaceBrandingService {
  current(): SurfaceBrandingState;
  subscribe(listener: () => void): () => void;
  view(scope: MountScope): SurfaceBrandingService;
  dispose(): void;
}

/** The members of a `/v1/auth/me` or `/v1/auth/providers` answer that branding reads. */
interface BrandingCarrier {
  readonly branding?: unknown;
}

/**
 * The `branding` member of a `/v1/auth/me` or `/v1/auth/providers` answer when it passes
 * `OrgBrandingSchema`, else `null`. The answer is untrusted, so its declared type proves nothing.
 * A JSON scalar or `null` answer reads `null` too.
 */
function brandingOf(answer: BrandingCarrier | null): OrgBranding | null {
  const parsed = OrgBrandingSchema.safeParse(typeof answer === 'object' && answer !== null ? answer.branding : undefined);
  return parsed.success ? parsed.data : null;
}

/**
 * Build the controller.
 *
 * Creation calls `loadDeploymentBranding()` once (`/branding.json` through the global `fetch`,
 * with no bearer; a missing, empty, invalid or slow file reads `null`, and one 1500 ms deadline
 * bounds its headers and its body). While the session hydrates
 * no org fetch runs. Authenticated, the org's branding is `GET /v1/auth/me`'s `branding`;
 * anonymous, `GET /v1/auth/providers`' `branding`, which core-api resolves by `Host`. Both reads go
 * through `createCoreApiRequest(platformFetch, timeoutMs)`, so `platformFetch` attaches the
 * session bearer and bounds the wait for the headers, and the body read after the headers has a
 * bound of `timeoutMs`. A `branding` member that fails `OrgBrandingSchema` reads `null`. A
 * rejected call, a non-2xx answer, a body that is not JSON and a body that stalls past the bound
 * read `null` too, so `resolved` turns `true` in bounded time. A change of `status`, `userId` or
 * `orgId` refetches; a token refresh alone does not. Each fetch captures a generation, and a
 * response overtaken by a later fetch writes nothing. Once resolved, every branding or polarity
 * change calls `applyBranding(branding, resolved polarity, deployment)`.
 *
 * @param session This origin's session port.
 * @param polarity The document's polarity controller.
 * @param platformFetch The policy's `platformFetch` (WS-14 section 3.6, change 3).
 * @param timeoutMs The body bound of each org read, {@link SERVICES_FETCH_TIMEOUT_MS} in
 *   production.
 */
export function createBrandingController(
  session: ServiceSession,
  polarity: PolarityController,
  platformFetch: typeof fetch,
  timeoutMs: number = SERVICES_FETCH_TIMEOUT_MS,
): BrandingController {
  const coreApi = createCoreApiRequest(platformFetch, timeoutMs);
  const listeners = createListenerSet();
  let state: SurfaceBrandingState = Object.freeze({ branding: null, deployment: null, resolved: false });
  let branding: OrgBranding | null = null;
  let deployment: DeploymentBranding | null = null;
  let orgSettled = false;
  let deploymentSettled = false;
  let identityKey: string | null = null;
  let generation = 0;
  let disposed = false;

  const apply = (): void => {
    if (state.resolved) applyBranding(state.branding, polarity.current().resolved, state.deployment);
  };
  const publish = (): void => {
    const resolved = orgSettled && deploymentSettled;
    if (state.branding === branding && state.deployment === deployment && state.resolved === resolved) return;
    state = Object.freeze({ branding, deployment, resolved });
    apply();
    listeners.notify();
  };

  const fetchOrgBranding = (): void => {
    const snapshot = session.current();
    if (snapshot.status === 'hydrating') return;
    const key = `${snapshot.status}\n${snapshot.userId ?? ''}\n${snapshot.orgId ?? ''}`;
    if (key === identityKey) return;
    identityKey = key;
    generation += 1;
    const captured = generation;
    const load: Promise<OrgBranding | null> =
      snapshot.status === 'authenticated'
        ? coreApi<BrandingCarrier | null>('/v1/auth/me', { method: 'GET', headers: { Accept: 'application/json' } }).then(
            brandingOf,
          )
        : coreApi<BrandingCarrier | null>('/v1/auth/providers', {
            method: 'GET',
            headers: { Accept: 'application/json' },
          }).then(brandingOf);
    void load
      .catch(() => null)
      .then((next) => {
        if (disposed || captured !== generation) return;
        branding = next;
        orgSettled = true;
        publish();
      });
  };

  void loadDeploymentBranding().then((next) => {
    if (disposed) return;
    deployment = next;
    deploymentSettled = true;
    publish();
  });
  const stopSession = session.subscribe(fetchOrgBranding);
  const stopPolarity = polarity.subscribe(apply);
  fetchOrgBranding();

  return {
    current: () => state,
    subscribe: (listener) => listeners.add(listener),
    view(scope) {
      return scope.guard({
        current: () => state,
        subscribe: (listener: () => void) => {
          const remove = listeners.add(listener);
          scope.own(remove);
          return remove;
        },
      });
    },
    dispose() {
      disposed = true;
      stopSession();
      stopPolarity();
      listeners.clear();
    },
  };
}

function setVar(el: HTMLElement, name: string, value: string | null): void {
  if (value) el.style.setProperty(name, value);
  else el.style.removeProperty(name);
}

function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Apply branding to `<html>`, `<head>` and `document.title`.
 *
 * `branding === null` falls back to the `deployment` tier, then to the neutral defaults (token
 * accent, neutral title and favicon). Precedence per field: per-org, then deployment, then the
 * hardwired neutral. It sets the `--color-accent` and `--color-accent-secondary` custom properties
 * for the resolved polarity, and the on-accent foregrounds `--color-on-accent` and
 * `--color-on-accent-secondary` (D23). A `null` value removes its property, so the CSS default
 * applies. Colour values are `#rrggbb` by the contract and go through `style.setProperty`, where a
 * custom-property value cannot break out into other CSS (security branch S7). It never throws.
 */
export function applyBranding(
  branding: OrgBranding | null,
  resolved: ResolvedPolarity,
  deployment: DeploymentBranding | null = null,
): void {
  const el = document.documentElement;
  const { accent, accentSecondary } = accentFor(branding, resolved, deployment);
  setVar(el, '--color-accent', accent);
  setVar(el, '--color-accent-secondary', accentSecondary);
  setVar(el, '--color-on-accent', onAccentFor(accent));
  setVar(el, '--color-on-accent-secondary', onAccentFor(accentSecondary));
  document.title = branding?.productName ?? deployment?.productName ?? NEUTRAL_PRODUCT_NAME;
  setFavicon(branding?.faviconUrl ?? deployment?.faviconUrl ?? NEUTRAL_FAVICON_HREF);
}

/** How long `/branding.json` may take, headers and body, before the deployment tier reads `null`, in milliseconds. */
const DEPLOYMENT_BRANDING_TIMEOUT_MS = 1500;

/**
 * Fetch and validate `/branding.json`, the deployment brand tier that nginx serves from the Helm
 * ConfigMap.
 *
 * One deadline of 1500 ms covers the whole read: the wait for the headers and the read of the
 * body. When it passes, the function aborts the request through the `signal` it handed
 * `fetchImpl` and resolves `null`, even when `fetchImpl` ignores the signal. So a body that stalls
 * after its headers cannot hold the deployment tier, and the branding controller's `resolved`
 * turns `true` in bounded time.
 *
 * @param fetchImpl The `fetch` to send through; the global `fetch` by default, so the request
 *   carries no bearer. It receives `{ cache: 'no-store', signal }`.
 * @returns The record when it passes `DeploymentBrandingSchema` and sets at least one field. `null`
 *   for an empty `{}`, a non-2xx answer, an invalid shape, a network error, a body that is not
 *   JSON, or no complete answer (headers and body) within 1500 ms. It never throws, and it keeps
 *   no module state.
 */
export async function loadDeploymentBranding(fetchImpl: typeof fetch = fetch): Promise<DeploymentBranding | null> {
  const abort = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      abort.abort(new DOMException(`no /branding.json within ${DEPLOYMENT_BRANDING_TIMEOUT_MS} ms`, 'TimeoutError'));
      resolve(null);
    }, DEPLOYMENT_BRANDING_TIMEOUT_MS);
  });
  const read = (async (): Promise<DeploymentBranding | null> => {
    const res = await fetchImpl('/branding.json', { cache: 'no-store', signal: abort.signal });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    const parsed = DeploymentBrandingSchema.safeParse(json);
    return parsed.success && Object.keys(parsed.data).length > 0 ? parsed.data : null;
  })().catch(() => null);
  try {
    return await Promise.race([read, deadline]);
  } finally {
    clearTimeout(timer);
  }
}
